<template>
  <page-wrapper error-key="tariffSchedules2" :transparent-bg="isGridViewMode">
    <template #title>
      <div class="h3">Ручное управление</div>
      <v-spacer />

      <view-mode-toggle v-if="!isEmpty && $vuetify.display.mdAndUp" v-model="viewMode" />
    </template>

    <template v-if="!isLoading && isEmpty">
      <page-content-placeholder
        v-if="noAccounts"
        text="Прежде чем добавить тарифный план вам необходимо создать аккаунт"
        :img-src="PlaceholderImage"
      >
        <create-account-dialog>
          <template #activator>
            <e-btn prepend-icon-src="plus" variant="primary" class="mt-6"> Создать аккаунт</e-btn>
          </template>
        </create-account-dialog>
      </page-content-placeholder>

      <page-content-placeholder
        v-else-if="!roomCategories.roomCategories.data?.length"
        text="У вас отсутствуют Тарифные планы"
        max-width="640px"
        :img-src="PlaceholderImage"
      >
        <div class="paragraph-m_h mt-1">
          Прежде чем создать тарифный план ручного управления вам необходимо добавить
          <router-link :to="{ name: RouteNames.RoomCategories }" class="link">Категории номеров.</router-link>
        </div>
      </page-content-placeholder>

      <page-content-placeholder v-else text="У вас отсутствуют Тарифные планы" :img-src="PlaceholderImage">
        <operation-button
          prepend-icon-src="plus"
          :to="createTariffTo"
          :operation="operations?.canAddExtranetTariffSchedule"
          variant="primary"
          class="mt-6"
        >
          Создать тарифный план
        </operation-button>
      </page-content-placeholder>
    </template>

    <template v-if="!isEmpty || isLoading" #secondRow>
      <div class="d-flex">
        <e-input v-model="filter.query" label="Поиск" class="controlled__search-input">
          <template #append-inner>
            <e-icon name="search" />
          </template>
        </e-input>

        <v-spacer />

        <operation-button
          prepend-icon-src="plus"
          :icon="$vuetify.display.smAndDown"
          :to="createTariffTo"
          :operation="operations?.canAddExtranetTariffSchedule"
          variant="primary"
          class="controlled__create-btn"
        >
          <span v-if="!$vuetify.display.smAndDown">Создать тарифный план</span>
        </operation-button>
      </div>
    </template>

    <template v-if="!isEmpty || isLoading">
      <div v-if="isGridViewMode" class="controlled__grid">
        <template v-if="isLoading">
          <skeleton-loader v-for="item in 10" height="200px" :key="item" />
        </template>
        <template v-else>
          <div
            v-for="tariff in filteredTariffs"
            :key="tariff.tariffScheduleCid"
            class="controlled__grid-item"
            @click="toEdit(tariff.tariffScheduleCid)"
          >
            <div class="d-flex justify-space-between mb-1">
              <tariff-status :status="tariff.status" />
              <tariffs-context-menu @edit="toEdit(tariff.tariffScheduleCid)" />
            </div>
            <div class="paragraph-m font-600 mb-4">{{ tariff.name[0].value }}</div>
            <div class="text-secondary d-flex align-center paragraph-s">
              <e-icon name="clock" :size="20" class="mr-2" />
              <span
                >{{ tariff.stopSalePeriodHours }}
                {{ declensionString(tariff.stopSalePeriodHours, ['час', 'часа', 'часов']) }}</span
              >
            </div>
          </div>
        </template>
      </div>

      <e-data-table
        v-else
        :headers="headers"
        :items="filteredTariffs || []"
        :loading="isLoading"
        interactive
        @on-row-click="rowClickHandler"
      >
        <template v-slot:[`item.status`]="{ item }">
          <tariff-status :status="item.status" />
        </template>
        <template v-slot:[`item.stopSalePeriodHours`]="{ value }">
          {{ value }} {{ declensionString(value, ['час', 'часа', 'часов']) }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <tariffs-context-menu @edit="toEdit(item.tariffScheduleCid)" />
        </template>
      </e-data-table>
    </template>
  </page-wrapper>
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { DataTableHeaders } from '@/core/types/components/table';
import PlaceholderImage from '@/assets/img/pages/object/handel_placeholder_image.svg';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import ViewModeToggle from '@/core/components/common/ViewModeToggle.vue';
import PageWrapper from '@/core/components/interface/PageWrapper.vue';
import SkeletonLoader from '@/core/components/interface/SkeletonLoader.vue';
import { EBtn, EDataTable, EIcon, EInput } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { useUserPreference } from '@/core/composables/useUserPreferences';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ViewMode } from '@/core/types/components/helps';
import { useComputedValue } from '@/core/utils/reactivity';
import { stringUrlSerializer, useQueryValue } from '@/core/utils/router/query-value';
import { containsSubstring, declensionString } from '@/core/utils/string';
import CreateAccountDialog from '@/parts/objects/components/CreateAccountDialog.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import { useRoomCategoriesStore } from '@/parts/room-categories/store/roomCategories';
import TariffStatus from '@/parts/tariffs/components/TariffStatus.vue';
import TariffsContextMenu from '@/parts/tariffs/components/TariffsContextMenu.vue';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const { accommodationObject } = useAccommodationObjectsStore();
const { getRoomCategories, roomCategories } = useRoomCategoriesStore();

const noAccounts = toRef(() => !accommodationObject.value?.contracts?.length);

const { getActiveTariffs, tariffsExtranet, operations } = useTariffsStore();
const $display = useDisplay();

const $route = useRoute();
const $router = useRouter();

const filter = ref({
  query: '',
});

const filteredTariffs = computed(() => {
  return tariffsExtranet.value?.filter((tariff) => {
    if (filter.value.query && !containsSubstring(tariff.name[0].value, filter.value.query)) {
      return false;
    }

    return true;
  });
});

useQueryValue(useComputedValue(filter, 'query'), 'query', stringUrlSerializer);

const isEmpty = toRef(() => !tariffsExtranet.value?.length);

const createTariffTo = toRef(() => ({ name: RouteNames.TariffCreate, params: { id: $route.params.id } }));

const viewMode = useUserPreference('controlledTariffsPageViewMode', ViewMode.Table);

const isGridViewMode = toRef(() => viewMode.value === ViewMode.Grid || $display.smAndDown.value);

const toEdit = (id: string) => {
  $router.push({
    name: RouteNames.TariffInfo,
    params: { id: $route.params.id, t_id: id },
  });
};

const rowClickHandler = (e: Event, row: { item: { tariffScheduleCid: string } }) => {
  toEdit(row.item.tariffScheduleCid);
};

const headers = computed<DataTableHeaders>(() => [
  {
    key: 'name[0].value',
    title: 'Тарифный план',
  },
  {
    key: 'stopSalePeriodHours',
    title: 'Релиз период',
  },
  {
    key: 'status',
    title: 'Статус',
  },
  {
    key: 'actions',
    align: 'end',
    sortable: false,
    width: $display.mdAndDown.value ? 40 : 60,
  },
]);

const { isLoading } = useFetch(async () => {
  await getActiveTariffs();
  await getRoomCategories(false);
});
</script>

<style scoped lang="scss">
.controlled {
  &__search-input {
    max-width: 280px;

    @include sm-and-down {
      max-width: 100%;
    }
  }

  &__create-btn {
    @include sm-and-down {
      position: fixed;
      right: $gap-4;
      bottom: $gap-4;
    }
  }

  &__grid {
    display: grid;

    grid-gap: $gap-4;
    grid-template-columns: repeat(4, 1fr);

    @include lg-and-down {
      grid-template-columns: repeat(3, 1fr);
    }

    @include sm-and-down {
      grid-template-columns: repeat(2, 1fr);
    }

    @include xs {
      grid-template-columns: 1fr;
    }

    &-item {
      min-height: 200px;
      padding: $gap-3 $gap-4 $gap-4;

      background-color: $main-bg-color;
      border-radius: $border-radius-l;

      cursor: pointer;

      @include hover {
        box-shadow: $border-box-shadow;
      }
    }
  }
}
</style>
